import {useState, useEffect} from "react"
import AuthStream from "../../streams/auth"
import {OnError, OnComplete} from "../../streams/auth/base"
import {Auth} from "../../types"
import authentication from "../.."
import {LogInAuthStream} from "./logInStateStream"

interface Props {
  onError?: OnError
  onComplete?: OnComplete
}

/**
 * Auth values are kept in storage and are built up over the duration of
 * authentication so many of these values may or may not be available
 */
type PartialAuth = Partial<Auth>

export default function useAuth({onComplete, onError}: Props = {}): PartialAuth {
  const [auth, setAuth] = useState<PartialAuth>(() => (authentication.isLoggedIn() ? AuthStream.get() : {}))

  useEffect(() => {
    const subscription = AuthStream.subscribe((newAuth) => setAuth(newAuth), onError, onComplete)

    return () => {
      return subscription.unsubscribe()
    }
  }, [onError, onComplete])

  return auth
}

export const useAuthIsGhost = () => {
  const isGhost = useAuth().productPermissions?.ghost?.isGhost
  return {
    isGhost,
    isNotGhost: isGhost === false // undefined until logged in, so don't use falsey value
  }
}

export const useIsLoggedIn = () => {
  const [authLoggedIn, setAuthLoggedIn] = useState(() => authentication.isLoggedIn())
  const [loggedIn, setLoggedIn] = useState(authLoggedIn)
  const auth = useAuth()

  useEffect(() => {
    if (!authLoggedIn) {
      setLoggedIn(false)
    } else if (authLoggedIn && auth) {
      // this LogInAuthStream stream fires before AuthStream, so we want to check
      // logged in AND auth data, otherwise consumers may check `isLoggedIn`
      // and then try to access auth data before it's available
      setLoggedIn(true)
    }
  }, [auth, authLoggedIn])

  useEffect(() => {
    const sub = new LogInAuthStream().stream.subscribe(({loggedInStream}) => {
      setAuthLoggedIn(!!loggedInStream)
    })

    return () => sub.unsubscribe()
  }, [])

  return loggedIn
}
